import _ from 'underscore';
import { serializeImageAssetMetadata, deserializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import { updatePlacementWithBaseCropSettings, updatePlacementWithPlacementSettings } from '@bingads-webui-campaign/image-experience-utils';

/* Restore image model to UI state
 * When there is effect applied, need to convert Metadata to effectSettings
 */
export const processImagePlacementsMetadata =
  (placements, baseImageIdStr, baseImageInfo) => {
    let imageLevelSettings;
    let propagateToImageLevel = false;

    const includedPlacements = _.filter(placements, placement => placement.IsIncluded);
    const excludedPlacements = _.filter(placements, placement => !placement.IsIncluded);
    const hasBeenBulkCropped = placements[0].baseImage.originalUrl &&
      placements[0].baseImage.originalUrl !== placements[0].baseImage.src;
    const hasPlacementSettings =
      placements[0].baseImage.placementSettings &&
      placements[0].baseImage.placementSettings.length > 0;

    const uniqueCountOfIds = _.chain(includedPlacements)
      .pluck('Id').uniq().size()
      .value();

    if (uniqueCountOfIds === 1) {
      // when placements have same effect, propagate it to image level
      propagateToImageLevel = true;
    }

    const cachedDeserializedMetadataById = {};

    _.each(includedPlacements, (placement) => {
      const cachedValue = cachedDeserializedMetadataById[placement.Id];
      let effectSettings;

      if (_.isUndefined(cachedValue)) {
        const deserializeMetadata = deserializeImageAssetMetadata(placement.Metadata) || {};

        cachedDeserializedMetadataById[placement.Id] = deserializeMetadata;
        ({ effectSettings } = deserializeMetadata);
      } else {
        ({ effectSettings } = cachedValue);
      }

      if (placement.IdString !== baseImageIdStr && effectSettings) {
        // restore placement various settings (effect, crop) in UI state
        const effectSrc = placement.Src;

        placement.setEffectSettings(_.defaults({ effectSrc }, effectSettings));
      }

      if (propagateToImageLevel && !imageLevelSettings) {
        imageLevelSettings = { effectSettings: placement.effectSettings };
      }
    });

    // restore image level UI state when all placements have same effect
    _.each(includedPlacements, (placement) => {
      const { url: originalSrc } = baseImageInfo;

      placement.BaseImage.setSrc(originalSrc);
      placement.BaseImage
        .setEffectSettings(imageLevelSettings && imageLevelSettings.effectSettings);
    });

    // let excluded placements inherit image level
    if (!_.isEmpty(includedPlacements)) {
      _.each(excludedPlacements, (placement) => {
        placement.setBaseImage(includedPlacements[0].BaseImage.clone());
        placement.setEffectSettings(placement.BaseImage.EffectSettings);
      });
    }

    if (hasBeenBulkCropped) {
      const { BaseImage } = placements[0];

      _.each(placements, (placement) => {
        updatePlacementWithBaseCropSettings({
          placement,
          mtImgObject: BaseImage,
        });
      });
    }

    if (hasPlacementSettings) {
      const { BaseImage } = placements[0];

      _.each(placements, (placement) => {
        updatePlacementWithPlacementSettings({
          placement,
          mtImgObject: BaseImage,
          updatePlacementSettings:
            !placement.isMaunalCropped(), // fill in placement settings when
          // crop settings are auto-filled in MSAN workflow
          isMultimediaAd: false,
          isMultimediaAdinMSANEnabled: true,
        });
      });
    }
  };

const DefaultKey = 'default';

const containMetadata = metadata => !_.isEmpty(metadata);
export const getMetadataKey = metadata => (containMetadata(metadata) ? metadata : DefaultKey);

export const collectExistingMetadata = assetList => _.reduce(assetList, (memo, asset) => {
  const key = asset.BaseId.toString();
  const original = memo[key];
  const metadataKey = getMetadataKey(asset.Metadata);
  const isProcessed = containMetadata(asset.Metadata);

  return {
    ...memo,
    [key]: _.extend({
      ...original,
      [metadataKey]: {
        // id and src should be matched
        // when apply effects, we only modify EffectSettings and keep src read-only.
        id: asset.Id,
        bingSrc: asset.Src,
      },
    }, isProcessed && {
      [DefaultKey]: {
        id: asset.BaseImage.Id,
        bingSrc: asset.BaseImage.Src,
      },
    }),
  };
}, {});

export const insertMetadata = (dict, id, metadata, data) => {
  const key = getMetadataKey(metadata);
  _.extend(dict[id.toString()], {
    [key]: {
      id: data.id,
      bingSrc: data.bingSrc,
    },
  });
};

export const findMetadata = (dict, id, metadata) => {
  const metadataKey = getMetadataKey(metadata);
  const assetsFromImage = dict[id.toString()];

  return assetsFromImage[metadataKey];
};

export const isMetadataExisting = (dict, id, metadata) => {
  const found = findMetadata(dict, id, metadata);

  return !_.isUndefined(found);
};

export function contertToLowerCaseKey(objs) {
  return objs.map((obj) => {
    const item = {};
    Object.keys(obj).forEach((key) => {
      item[key.charAt(0).toLowerCase() + key.substring(1)] = obj[key];
    });

    return item;
  });
}

const removeRatioTypeInId = (flattenImageList) => {
  flattenImageList.forEach((image) => {
    // eslint-disable-next-line no-param-reassign
    image.id = image.id.toString().indexOf('-') > -1 && image.isLogo ? image.id.match(/(\S*)-/)[1] : image.id;
  });
  return flattenImageList;
};

export const getFlattenedImageAssetList = (imageList, isIncluded) => {
  let filteredImageList = imageList;

  if (isIncluded) {
    filteredImageList = _.map(
      imageList,
      imageArray => _.filter(imageArray, imageObj => imageObj.isIncluded)
    );
  }
  const flattenImageList = _.flatten(filteredImageList, 1);
  return removeRatioTypeInId(flattenImageList);
};

// flattenedImageAssetList and flattenedImageAssetsPayload are mutually exclusive
// only one will be used at one time
export const getImagesDataModel = ({
  flattenedImageAssetList, updates, flattenedImageAssetsPayload, UiUnsupportedRatios,
}) => {
  const imagesModel = flattenedImageAssetsPayload || _.map(
    flattenedImageAssetList,
    (imageObj, index) => {
      const overrideInfo = updates && updates[index];
      const model = imageObj.formatToSave(false, false, overrideInfo);

      return model;
    }
  );

  const UIUnSupportedImagesModel = contertToLowerCaseKey(UiUnsupportedRatios
    .map(association =>
      _.pick(association.Asset, ['Id', 'CropWidth', 'CropHeight', 'CropX', 'CropY', 'IdString', 'SubType'])));

  return {
    Images: JSON.stringify([...imagesModel, ...UIUnSupportedImagesModel]),
  };
};

export const setMTImageModel = (
  originalData,
  updates = {},
  imageList,
  UiUnsupportedRatios
) => {
  const flattenedImageList = getFlattenedImageAssetList(imageList, false);

  _.extend(originalData, getImagesDataModel({
    flattenedImageAssetList: flattenedImageList,
    updates,
    UiUnsupportedRatios,
  }));
};

// If applying effect to an image, need to upload to get a new image Id
export const updateAssetsIdsAsync
  = (flattenedImageAssetList, imageIndexAsThumbnail, imageOData) => {
    const updates = {};

    const assetPositionsToUpdate = [];
    const newAssets = [];

    // use this mapping to update id. collect metadata-id mapping from existing assets
    const existingMetadataIdMapping = collectExistingMetadata(flattenedImageAssetList);

    _.each(flattenedImageAssetList, (asset, index) => {
      const metadata = serializeImageAssetMetadata({ effectSettings: asset.EffectSettings });

      // check if any assets have updates in metadata, if so, need to update id
      if (asset.isEffectEdited()) {
        assetPositionsToUpdate.push({ position: index, baseId: asset.BaseId, metadata });

        if (!isMetadataExisting(existingMetadataIdMapping, asset.BaseId, metadata)) {
          newAssets.push({
            url: asset.FinalSrc,
            baseId: asset.BaseId,
            metadata,
          });
        }
      }
    });

    if (!_.isEmpty(assetPositionsToUpdate)) {
      const saveNewImagesPromise = _.isEmpty(newAssets)
        ? Promise.resolve([]) : imageOData.bulkSaveImagesByUrl(newAssets);

      return saveNewImagesPromise
        .then((ids) => {
          const hasError = _.some(ids, id => id && id.code);

          if (hasError) {
            const error = {
              errors: _.compact(_.map(ids, id => id.code && {
                Error: id.code,
              })),
            };

            // reject to reuse common catch logic
            return Promise.reject(error);
          }

          _.each(ids, (id, index) => {
            const { baseId, metadata } = newAssets[index];
            insertMetadata(existingMetadataIdMapping, baseId, metadata, {
              id,
              bingSrc: undefined, // for new assets, we don't have bing url yet
            });
          });

          _.each(assetPositionsToUpdate, ({ position, baseId, metadata }) => {
            const foundInfo = findMetadata(existingMetadataIdMapping, baseId, metadata);

            updates[position] = {
              id: foundInfo.id,
              bingSrc: foundInfo.bingSrc,
            };
          });

          // if asset used for thumbnail is updated without latest bing src, send call
          if (updates[imageIndexAsThumbnail] && !updates[imageIndexAsThumbnail].bingSrc) {
            const thumbnailAsset = updates[imageIndexAsThumbnail];

            return imageOData.getImageByIdAsync(thumbnailAsset.id)
              .then((value) => {
                thumbnailAsset.bingSrc = value.Url;

                return updates;
              });
          }

          return updates;
        });
    }

    return Promise.resolve(updates);
  };
